import React from 'react';
import { Dropdown, Grid, Popup, Icon } from 'semantic-ui-react';
import { Field, FormikProps } from 'formik';
import { FieldLabel } from '../../../../styles';
import Input from '../../../../components/Input';
import { batchClosingDayOptions } from '../../types';
import { successNotification } from '../../../../services/notification';
import { unmaskMoney } from '../../../../services/masks';
import CoveragesConfigModal from './ModalContent';
import Toggle from '../../../../components/Toggle';

export type YelumFireInsuranceConfigProps = FormikProps<any> & {
  handleInputChange: Function;
};

const YelumFireInsuranceConfig = (props: YelumFireInsuranceConfigProps) => {
  const { setFieldValue, handleInputChange } = props;
  const yelum = props?.values?.insurerSpecificData?.yelum?.fireInsurance;

  // Função para calcular o dia preferencial de transmissão baseado na data de vencimento
  // Regra: Transmissão deve ser 10 dias antes do vencimento
  // Se vencimento <= 10: transmissão no último dia do mês anterior (dia 25-31, usando 25 como padrão)
  // Se vencimento > 10: transmissão = vencimento - 10 (mesmo mês)
  const calculateTransmissionDay = (dueDate: number): number | null => {
    if (!dueDate || dueDate < 1 || dueDate > 31) {
      return null;
    }

    if (dueDate <= 10) {
      // Transmissão no mês anterior: usar último dia útil (aproximação: dia 25)
      // Na prática, será o último dia do mês anterior, mas como não temos contexto de mês,
      // usamos 25 como valor padrão que será ajustado no backend
      return 25;
    } else {
      // Transmissão no mesmo mês: 10 dias antes do vencimento
      const transmissionDay = dueDate - 10;
      return transmissionDay >= 1 ? transmissionDay : 1;
    }
  };

  const handleDueDateChange = (value: number) => {
    setFieldValue('insurerSpecificData.yelum.fireInsurance.dueDate', value);

    // Calcular automaticamente o dia preferencial de transmissão
    const transmissionDay = calculateTransmissionDay(value);
    if (transmissionDay) {
      setFieldValue('insurerSpecificData.yelum.fireInsurance.lotClosingDay', transmissionDay);
    }
  };

  return (
    <Grid>
      <Grid.Row columns={'equal'}>
        <Grid.Column>
          <FieldLabel>Código Parceiro *</FieldLabel>
          <Field
            value={yelum.code}
            name="insurerSpecificData.yelum.fireInsurance.code"
            onChange={(e: React.ChangeEvent<HTMLInputElement>, data: any) => {
              handleInputChange(data, props);
            }}
            onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue(
                'insurerSpecificData.yelum.fireInsurance.code',
                e.target.value
              );
            }}
            component={Input}
            fluid
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row columns={'equal'}>
        <Grid.Column>
          <FieldLabel>Comissão Corretora (%) *</FieldLabel>
          <Field
            value={yelum?.brokerCommission === 0 ? '' : yelum?.brokerCommission}
            name="insurerSpecificData.yelum.fireInsurance.brokerCommission"
            component={Input}
            onChange={(e: React.ChangeEvent<HTMLInputElement>, data: any) => {
              setFieldValue(data.field.name, data.value);
            }}
            onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value.replace(/,/g, '.').replace(/[^\d.]/g, '');

              if (Number(value) > 100) {
                setFieldValue(
                  'insurerSpecificData.yelum.fireInsurance.brokerCommission',
                  Number(100).toFixed(2)
                );
                return;
              }

              if (Number(value) < 0) {
                setFieldValue(
                  'insurerSpecificData.yelum.fireInsurance.brokerCommission',
                  0
                );
                return;
              }
              setFieldValue(
                'insurerSpecificData.yelum.fireInsurance.brokerCommission',
                Number(value).toFixed(2)
              );
            }}
            type="number"
            max={100.0}
            min={1.0}
            fluid
          />
        </Grid.Column>
        <Grid.Column>
          <FieldLabel>Pró-labore (%) *</FieldLabel>
          <Field
            value={yelum?.proLabore === 0 ? '' : yelum?.proLabore}
            name="insurerSpecificData.yelum.fireInsurance.proLabore"
            component={Input}
            onChange={(e: React.ChangeEvent<HTMLInputElement>, data: any) => {
              setFieldValue(data.field.name, data.value);
            }}
            onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value.replace(/,/g, '.').replace(/[^\d.]/g, '');

              if (Number(value) > 100) {
                setFieldValue(
                  'insurerSpecificData.yelum.fireInsurance.proLabore',
                  Number(100).toFixed(2)
                );
                return;
              }

              if (Number(value) < 0) {
                setFieldValue('insurerSpecificData.yelum.fireInsurance.proLabore', 0);
                return;
              }
              setFieldValue(
                'insurerSpecificData.yelum.fireInsurance.proLabore',
                Number(value).toFixed(2)
              );
            }}
            type="number"
            max={100.0}
            min={1.0}
            fluid
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row columns={'equal'}>
        <Grid.Column>
          <FieldLabel>
            Data de Vencimento do Boleto *
            <Popup
              content={
                'Dia do mês em que o boleto vencerá (ex: dia 5, 10, 15). Este campo é usado para calcular automaticamente o dia preferencial de transmissão.'
              }
              position={'top left'}
              trigger={
                <Icon
                  name={'info circle'}
                  color={'blue'}
                  style={{ cursor: 'help', marginLeft: 5 }}
                />
              }
            />
          </FieldLabel>
          <Dropdown
            style={{
              width: '100%'
            }}
            value={yelum?.dueDate}
            name={`insurerSpecificData.yelum.fireInsurance.dueDate`}
            options={batchClosingDayOptions}
            placeholder={'Selecionar...'}
            search
            selection
            onChange={(_, { value }) => {
              handleDueDateChange(value as number);
            }}
            clearable
          />
        </Grid.Column>
        <Grid.Column>
          <FieldLabel>
            Dia Preferencial de Transmissão *
            <Popup
              content={
                'Calculado automaticamente como 10 dias antes do vencimento, garantindo o prazo mínimo de 7 dias da Yelum. Se o vencimento for até o dia 10, a transmissão ocorrerá no mês anterior, ampliando a janela de retroatividade para 60 dias. Se for após o dia 10, a transmissão será no mês do vencimento, com janela de 30 dias.'
              }
              position={'top left'}
              trigger={
                <Icon
                  name={'info circle'}
                  color={'blue'}
                  style={{ cursor: 'help', marginLeft: 5 }}
                />
              }
            />
          </FieldLabel>
          <Dropdown
            style={{
              width: '100%'
            }}
            value={yelum?.lotClosingDay}
            name={`insurerSpecificData.yelum.fireInsurance.lotClosingDay`}
            options={batchClosingDayOptions}
            placeholder={'Selecionar...'}
            search
            selection
            onChange={(_, { value }) => {
              setFieldValue(
                `insurerSpecificData.yelum.fireInsurance.lotClosingDay`,
                value
              );
            }}
            clearable
            disabled={!!yelum?.dueDate}
          />
        </Grid.Column>
      </Grid.Row>

      <Grid.Row columns={'equal'}>
        <Grid.Column>
          <FieldLabel>Comissão na Assistência?</FieldLabel>
          <Toggle
            name="insurerSpecificData.yelum.fireInsurance.applyCommissionOnAssistance"
            checked={true}
            disabled
            label={'SIM'}
          />
        </Grid.Column>
        <Grid.Column>
          <FieldLabel>
            Parcelamento Flexível
            <Popup
              content={
                <div style={{ maxWidth: '300px', lineHeight: '1.4' }}>
                  <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>
                    O que muda?
                  </p>
                  <p style={{ margin: '0' }}>
                    <strong>Desativado:</strong> O seguro será sempre cobrado em 12x fixo.<br />
                    <strong>Ativado:</strong> A imobiliária pode escolher cobrar em menos parcelas (1x a 12x)
                    e fazer a conciliação pelo módulo de Faturas.
                  </p>
                </div>
              }
              position={'top left'}
              trigger={
                <Icon
                  name={'info circle'}
                  color={'blue'}
                  style={{ cursor: 'help', marginLeft: 5 }}
                />
              }
            />
          </FieldLabel>
          <Toggle
            name="insurerSpecificData.yelum.fireInsurance.flexibleInstallmentsEnabled"
            checked={yelum?.flexibleInstallmentsEnabled}
            onChange={() => {
              setFieldValue(
                'insurerSpecificData.yelum.fireInsurance.flexibleInstallmentsEnabled',
                !yelum?.flexibleInstallmentsEnabled
              );
            }}
            label={yelum?.flexibleInstallmentsEnabled ? 'SIM' : 'NÃO'}
          />
        </Grid.Column>
      </Grid.Row>

      <Grid.Row columns={'equal'}>
        <Grid.Column width={16}>
          <CoveragesConfigModal {...props} />
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export default YelumFireInsuranceConfig;
